import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetSimDecksQuery } from "@api/apiV6";
import { useSelector } from "react-redux";
import BuilderHeader from "@components/BuilderHeader.react";
import { Image, Modal, Icon } from "semantic-ui-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import _ from "lodash";
import { Div } from "@components/Generics.react";
import "@styles/analytics.scss";
import { nameToHex, getAvatar } from "@utilities/helpers";
import RadarChart from "@components/charts/RadarChart.react";
import { advice } from "@schemas/sim_results";

export default function SimIndividualReport(props) {
    const { open, setOpen, user, course } = props;
    const { t } = useTranslation("common");
    const owner = useSelector((state) => state.auth.user);
    const { data: reports } = useGetSimDecksQuery({
        course_id: course.id,
        user_id: user.id,
    });

    const [processing, setProcessing] = useState(false);

    function downloadReportPage(name) {
        setProcessing(true);
        let reportPageView = document.querySelector(".report-page");
        html2canvas(reportPageView, { useCORS: true }).then((canvas) => {
            let imgData = canvas.toDataURL("image/png");
            var pdf = new jsPDF("p", "mm", "a4", true);
            var width = pdf.internal.pageSize.getWidth();
            var height = pdf.internal.pageSize.getHeight();
            pdf.addImage(imgData, "PNG", 0, 0, width, height, "", "SLOW");
            pdf.save((name + ".pdf").replace(/ /g, "_").toLowerCase());
            setProcessing(false);
        });
    }
    const banner = (
        <Div relative className="bannerSection" txtWhite>
            {user && (
                <Image
                    src={
                        "/assets/images/reportBanners/" +
                        nameToHex(`${user?.name}`)[2] +
                        ".jpg"
                    }
                />
            )}
            <Div className="overlay" />
            {user && (
                <Div className="profileImage">
                    {getAvatar(`${user?.name}`, 160)}
                </Div>
            )}
            <Div
                absolute
                right-aligned
                big
                uppercase
                wd="50%"
                style={{ right: "0", top: 0, zIndex: 1001 }}
                flex
            >
                <Div basepad wd="calc(100% - 112.5px)">
                    <Div huge altText>
                        {course.name}
                    </Div>
                </Div>
                <Div wd="107.5px" superpad charcoal>
                    <Image fluid src={owner.branding?.logo} alt="Brand Logo" />
                </Div>
            </Div>
            <Div
                uppercase
                bold
                basepad
                txtMidnight
                absolute
                flex
                spaceBetween
                style={{
                    left: "190px",
                    bottom: "-75px",
                    width: "calc(100% - 200px)",
                }}
            >
                <Div massive>{`${user?.name}`}</Div>
                {processing ? (
                    <Div large>{t("analytics.processing")}</Div>
                ) : (
                    <Div large clickable>
                        <Icon
                            circular
                            inverted
                            name="print"
                            onClick={() =>
                                downloadReportPage(
                                    `${course.name}-${user?.name}`
                                )
                            }
                        />
                        {/* <Icon
                            circular
                            inverted
                            name="mail"
                            onClick={mailReportPage}
                        /> */}
                    </Div>
                )}
            </Div>
        </Div>
    );

    const medians = {
        "Research Phase": 3.3,
        "Marathon Conceptualization": 4.3,
        "Sponsorship Campaign": 1.4,
        "Launch the Marathon": 4.2,
        "Marketing Campaigns": 3.7,
        "Event Planner": 2.5,
        "The Marathon Event": 4,
    };

    const competencyScores = _.orderBy(
        _.map(reports, (r) => ({
            competency: r.entity_name,
            value: parseInt(r.currentState.score * 100.0) / 100,
        })),
        ["value"],
        ["desc"]
    );

    const swotScores = _.orderBy(
        _.map(reports, (r) => ({
            competency: r.entity_name,
            value:
                parseInt(r.currentState.score * 100.0) / 100 -
                medians[r.entity_name],
        })),
        ["value"],
        ["desc"]
    );

    const adviceSuggestions = _.map(swotScores, (c) => {
        const competency = _.find(advice, (a) => a.engine === c.competency);
        return { advice: competency, score: c.value };
    });

    console.log("SWOT", swotScores);

    const metaSuggestions = [
        {
            title: "Strengths",
            data: _.map(
                _.filter(adviceSuggestions.slice(0, 3), (a) => a.score > 0),
                "advice.strength"
            ),
        },
        {
            title: "Weaknesses",
            data: _.map(adviceSuggestions.slice(4, 7), "advice.weakness"),
        },
        {
            title: "Opportunities",
            data: _.map(adviceSuggestions.slice(4, 7), "advice.opportunity"),
        },
        {
            title: "Threats",
            data: _.map(adviceSuggestions.slice(6, 7), "advice.threat"),
        },
    ];
    const suggestionMap = _.map(metaSuggestions, (s, idx) => (
        <Div key={`suggestion-${idx}`} wd="49%" ht="48%" ash gutter basepad>
            <Div bold uppercase>
                {s.title}
            </Div>
            <Div small>
                <ul style={{ paddingLeft: "15px" }}>
                    {_.map(s.data, (ss, idy) => (
                        <li key={`subsuggestion-${idx}-${idy}`}>{ss}</li>
                    ))}
                </ul>
            </Div>
        </Div>
    ));

    const topPanel = (
        <Div basepad wd="calc(100% + 15px)">
            <Div basepad fluid peat rounded>
                <Div fluid ht="400px" flex spaceBetween white gutter rounded>
                    <Div fluid spaceBetween rounded superpad gutter>
                        <Div huge gutter uppercase center-align>
                            Performance Benchmarks
                        </Div>
                        <Div fit-content centered small>
                            <RadarChart
                                name="Score"
                                width={600}
                                height={300}
                                data={competencyScores}
                            />
                        </Div>
                    </Div>
                </Div>
                <Div
                    white
                    fluid
                    spaceBetween
                    rounded
                    superpad
                    gutter
                    ht="550px"
                >
                    <Div huge trench uppercase center-align>
                        Improvement Suggestions
                    </Div>
                    <Div
                        fluid
                        ht="calc(100% - 50px)"
                        centered
                        flex
                        spaceBetween
                    >
                        {suggestionMap}
                    </Div>
                </Div>
            </Div>
        </Div>
    );

    const pageContent = (
        <Div
            txtMidnight
            big
            style={{
                margin: "90px 25px 0 10px",
            }}
        >
            <Div compact gutter nudgeLeft>
                <BuilderHeader
                    image={course?.image}
                    header={` ${course?.name}`}
                    description={` ${course?.description}`}
                />
            </Div>

            <Div>
                <Div clear fluid>
                    {topPanel}
                </Div>
            </Div>
        </Div>
    );

    return (
        <Modal
            basic
            className="analytics-page"
            onClose={() => setOpen(false)}
            open={open}
        >
            <Div
                className="close-btn"
                danger
                basepad
                rightRounded
                clickable
                onClick={() => setOpen(false)}
            >
                <Icon name="close" />
            </Div>
            <Div className="report-page">
                <Div fluid fullht white>
                    {banner}
                    {pageContent}
                </Div>
            </Div>
        </Modal>
    );
}
